import { c as _c } from "react/compiler-runtime";
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Type } from '@repo/design-system/components/ui/type';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppSelector } from '@repo/store/maps/hooks';
import { selectAskAnythingResult } from '@repo/store/maps/slices/clientContext';
import { selectMapData, selectSteamId } from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { getFontInfoForGlyph, getIconForGlyph, getSubcategoryIconStyle } from '@repo/types/maps/map-utils';
import { getSteamImageUrl } from '@repo/utility/image-urls';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { buildCategoryMap, buildSubcategoryMap } from '../checklist/checklist-utils';
import { PinListSidebarShell } from '../checklist/pin-list-sidebar-shell';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { categoryIconClass, getFeatureImageUrl } from './explore-sidebar-view';
interface AskAnythingSidebarViewProps {
  onBack: () => void;
  isMobilePane?: boolean;
  onSearchFocus?: () => void;
}
export const AskAnythingSidebarView: React.FC<AskAnythingSidebarViewProps> = t0 => {
  const $ = _c(77);
  const {
    onBack,
    isMobilePane: t1,
    onSearchFocus
  } = t0;
  const isMobilePane = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const result = useAppSelector(selectAskAnythingResult);
  const mapData = useAppSelector(selectMapData);
  const steamId = useAppSelector(selectSteamId);
  const {
    selectMarkerInMap
  } = useMapPinOperations();
  let t3;
  let t4;
  if ($[1] !== mapData || $[2] !== onBack || $[3] !== result) {
    t3 = () => {
      if (mapData && !result) {
        onBack();
      }
    };
    t4 = [mapData, result, onBack];
    $[1] = mapData;
    $[2] = onBack;
    $[3] = result;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  bb0: {
    if (!mapData) {
      t5 = null;
      break bb0;
    }
    let t6;
    if ($[6] !== mapData) {
      t6 = buildSubcategoryMap(mapData);
      $[6] = mapData;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    let t7;
    if ($[8] !== mapData) {
      t7 = buildCategoryMap(mapData);
      $[8] = mapData;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    if ($[10] !== t6 || $[11] !== t7) {
      t8 = {
        subcategoryMap: t6,
        categoryMap: t7
      };
      $[10] = t6;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t5 = t8;
  }
  const lookups = t5;
  let t6;
  if ($[13] !== mapData) {
    bb1: {
      const map = new Map();
      if (!mapData) {
        t6 = map;
        break bb1;
      }
      for (const feature of mapData.geojson.features) {
        if (feature.geometry.type === "Point") {
          map.set(feature.properties.externalId, feature);
        }
      }
      t6 = map;
    }
    $[13] = mapData;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const featureMap = t6;
  let t7;
  if ($[15] !== featureMap || $[16] !== result) {
    t7 = result ? featureMap.get(result.anchorPinId) ?? null : null;
    $[15] = featureMap;
    $[16] = result;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const anchorPin = t7;
  let t8;
  bb2: {
    if (!result) {
      let t9;
      if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = [];
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      t8 = t9;
      break bb2;
    }
    let t9;
    if ($[19] !== featureMap || $[20] !== result.pinIds) {
      let t10;
      if ($[22] !== featureMap) {
        t10 = pinId => featureMap.get(pinId);
        $[22] = featureMap;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      t9 = result.pinIds.map(t10).filter(_temp);
      $[19] = featureMap;
      $[20] = result.pinIds;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    t8 = t9;
  }
  const pinFeatures = t8;
  let t9;
  bb3: {
    const t10 = anchorPin ?? undefined;
    let t11;
    if ($[24] !== t10) {
      t11 = getFeatureImageUrl(t10);
      $[24] = t10;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    const anchorImage = t11;
    if (anchorImage) {
      t9 = anchorImage;
      break bb3;
    }
    let t12;
    if ($[26] !== pinFeatures) {
      const fallback = pinFeatures.find(_temp2);
      t12 = getFeatureImageUrl(fallback);
      $[26] = pinFeatures;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    const fallbackImage = t12;
    if (fallbackImage) {
      t9 = fallbackImage;
      break bb3;
    }
    let t13;
    if ($[28] !== steamId) {
      t13 = steamId ? getSteamImageUrl(steamId, "hero") : "";
      $[28] = steamId;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    t9 = t13;
  }
  const backgroundImageUrl = t9;
  if (!result) {
    return null;
  }
  let anchorCategory;
  let iconChar;
  let t10;
  if ($[30] !== anchorPin || $[31] !== lookups?.categoryMap || $[32] !== lookups?.subcategoryMap) {
    const anchorSubcategory = anchorPin ? lookups?.subcategoryMap.get(anchorPin.properties.subcategoryExternalId) ?? null : null;
    anchorCategory = anchorSubcategory ? lookups?.categoryMap.get(anchorSubcategory.categoryExternalId) ?? null : null;
    const iconGlyph = anchorSubcategory?.icon ?? "category";
    const fontInfo = getFontInfoForGlyph(iconGlyph);
    iconChar = getIconForGlyph(iconGlyph);
    t10 = getSubcategoryIconStyle(fontInfo.fontName);
    $[30] = anchorPin;
    $[31] = lookups?.categoryMap;
    $[32] = lookups?.subcategoryMap;
    $[33] = anchorCategory;
    $[34] = iconChar;
    $[35] = t10;
  } else {
    anchorCategory = $[33];
    iconChar = $[34];
    t10 = $[35];
  }
  const iconFontStyle = t10;
  const pinColor = anchorCategory?.color ?? DEFAULT_PIN_COLOR;
  let t11;
  if ($[36] !== result.query || $[37] !== t) {
    t11 = t(_temp3, {
      query: result.query
    });
    $[36] = result.query;
    $[37] = t;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const title = t11;
  let t12;
  if ($[39] !== result.answer || $[40] !== t) {
    t12 = result.answer ?? t(_temp4);
    $[39] = result.answer;
    $[40] = t;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  const description = t12;
  let t13;
  if ($[42] !== iconFontStyle || $[43] !== pinColor) {
    t13 = {
      backgroundColor: pinColor,
      ...iconFontStyle
    };
    $[42] = iconFontStyle;
    $[43] = pinColor;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== anchorPin || $[46] !== selectMarkerInMap) {
    t14 = () => anchorPin && selectMarkerInMap(anchorPin.properties.externalId, {
      flyTo: true,
      selectSource: "sidebar_ask_anything"
    });
    $[45] = anchorPin;
    $[46] = selectMarkerInMap;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const t15 = anchorPin?.properties.title || title;
  const t16 = anchorPin?.properties.title || title;
  const t17 = !anchorPin;
  let t18;
  if ($[48] !== iconChar || $[49] !== t13 || $[50] !== t14 || $[51] !== t15 || $[52] !== t16 || $[53] !== t17) {
    t18 = <button type="button" className={categoryIconClass} style={t13} onClick={t14} title={t15} aria-label={t16} disabled={t17}>{iconChar}</button>;
    $[48] = iconChar;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
    $[52] = t16;
    $[53] = t17;
    $[54] = t18;
  } else {
    t18 = $[54];
  }
  let t19;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = css({
      textAlign: "center",
      wordBreak: "break-word"
    });
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t19 || $[57] !== title) {
    t20 = <Type variant="header-m" color="white" className={t19} title={title}>{title}</Type>;
    $[56] = t19;
    $[57] = title;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = css({
      textAlign: "center"
    });
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== description || $[61] !== t21) {
    t22 = <Type variant="body-s" color="whiteAlpha.70" className={t21}>{description}</Type>;
    $[60] = description;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t20 || $[64] !== t22) {
    t23 = <FlexColumn align="center" gap="4" width="full">{t20}{t22}</FlexColumn>;
    $[63] = t20;
    $[64] = t22;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t18 || $[67] !== t23) {
    t24 = <FlexColumn align="center" gap="12" width="full" data-drawer-drag="true">{t18}{t23}</FlexColumn>;
    $[66] = t18;
    $[67] = t23;
    $[68] = t24;
  } else {
    t24 = $[68];
  }
  const hero = t24;
  const t25 = `${result.anchorPinId}:${result.query}`;
  let t26;
  if ($[69] !== backgroundImageUrl || $[70] !== hero || $[71] !== isMobilePane || $[72] !== onBack || $[73] !== onSearchFocus || $[74] !== pinFeatures || $[75] !== t25) {
    t26 = <PinListSidebarShell backgroundImageUrl={backgroundImageUrl} isMobilePane={isMobilePane} onBack={onBack} onSearchFocus={onSearchFocus} pinFeatures={pinFeatures} hero={hero} selectSource="sidebar_ask_anything" boundsFitKey={t25} />;
    $[69] = backgroundImageUrl;
    $[70] = hero;
    $[71] = isMobilePane;
    $[72] = onBack;
    $[73] = onSearchFocus;
    $[74] = pinFeatures;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  return t26;
};
function _temp(pin) {
  return !!pin;
}
function _temp2(pin_0) {
  return getFeatureImageUrl(pin_0);
}
function _temp3($) {
  return $.explore_menu.ask_results_title;
}
function _temp4($_0) {
  return $_0.explore_menu.ask_results_description;
}